<template>
  <div>
    <!-- Quotation Header (for update mode) -->
    <div
      class="d-flex flex-column flex-md-row title align-md-center"
      v-if="!isCreate"
    >
      <div>Quotation Number: #{{ item.quote_number }}</div>
      <v-spacer></v-spacer>
    </div>
    
    <!-- Essential Fields Row -->
    <v-row
      :class="$root.isMobile ? 'mt-4' : 'mt-2'"
      class="no-col-padding"
      :no-gutters="$root.isMobile"
    >
      <!-- Job Type - Essential for estimates -->
      <v-col class="d-flex" cols="12" :sm="4">
        <v-autocomplete
          outlined
          label="Job Type"
          :dense="$root.dense"
          item-text="name"
          item-value="id"
          :rules="rules"
          @change="onInput"
          :items="jobTemplates"
          v-model="form.template"
        >
        </v-autocomplete>
      </v-col>
      
      <!-- Technicians - Useful for estimates -->
      <v-col cols="12" :sm="4">
        <v-autocomplete
          outlined
          label="Technicians"
          :dense="$root.dense"
          item-text="name"
          item-value="phone"
          @change="onTechnicianChange"
          clearable
          :items="list"
          v-model="form.technician"
        ></v-autocomplete>
      </v-col>
      
      <!-- Appointment - Useful for scheduling estimates -->
      <v-col cols="12" :sm="4">
        <div class="d-flex">
          <v-checkbox
            class="pt-0 mt-2"
            @change="onInput"
            v-model="form.is_scheduled"
            hide-details=""
          ></v-checkbox>
          <date-time-picker
            @change="onInput"
            class="flex-1"
            :disabled="!form.is_scheduled"
            v-model="form.scheduled_at"
            ref="apt"
            :endAt="form.schedule_end_at"
            @onEndSelect="form.schedule_end_at = $event"
            label="Appointment"
          ></date-time-picker>
        </div>
      </v-col>
      
      <!-- VIN Number - Keep for auto estimates -->
      <v-col cols="12" :sm="4" v-if="isAutoJob">
        <v-text-field
          label="VIN Number"
          :dense="$root.dense"
          class="address_input"
          v-model="form.vin_number"
          @change="onInput"
          outlined
        >
          <template v-slot:append-outer>
            <v-btn
              depressed
              class="primary"
              @click="decodeVin()"
              tile
              :width="is_mobile ? '' : '120px'"
              color="white"
              :height="$root.dense ? '40px' : '56px'"
            >
              Decode VIN
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    
    <!-- Total Price Section - Essential for estimates -->
    <div class="d-flex pa-4 px-2 gap-4">
      <v-text class="title">Total Estimate Price ($): {{ total || 0 }}</v-text>
      
      <v-row class="d-flex align-center ml-4" dense>
        <v-col cols="12" :sm="2">
          <v-btn-toggle
            v-model="payment"
            class="toggle-group"
            mandatory
            @change="changePaymentType"
            color="green"
          >
            <v-btn :value="'cash'" color="white" depressed tile small>{{ paymentOptions[0].name }}</v-btn>
            <v-btn :value="'credit_card'" color="white" depressed tile small>{{ paymentOptions[1].name }}</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </div>
    
    <!-- VIN Decode Results -->
    <v-alert v-model="alert" dismissible color="primary" dense outlined>
      <div v-html="vinDetails"></div>
    </v-alert>
    
    <v-divider></v-divider>
    
    <!-- Technician Zone Info - Keep for reference -->
    <technician-zone-info
      @onTechnicianClick="form.technician = $event"
      ref="zone"
    ></technician-zone-info>
  </div>
</template>

<script>
import DateTimePicker from "../../DateTimeRangePicker.vue";
import {
  formatMessage,
  formatPhone,
  // getDateOnly,
} from "../../../assets/js/common";
import { auth } from "../../../assets/js/firebase";
import axios from "axios";
import TechnicianZoneInfo from "./TechnicianZoneInfo.vue";
// import customerModal from "../../../assets/js/customer.modal";
import { EventBus } from "../../../main";
import customerMixins from "../../../assets/js/customer.mixins";

export default {
  components: {
    DateTimePicker,
    TechnicianZoneInfo,
  },
  props: {
    value: {
      type: Object,
      default: () => {
        return {};
      },
    },
    isAutoJob: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: () => {
        return {};
      },
    },
    total: [String, Number],
  },
  mixins: [customerMixins],
  data() {
    return {
      vinDetails: "",
      alert: false,
      paymentOptions: [
        { value: "cash", name: "Cash" },
        { value: "credit_card", name: "Credit/Debit" },
      ],
      payment: "credit_card",
      form: {
        template: "",
        technician: "",
        is_scheduled: false,
        scheduled_at: "",
        schedule_end_at: "",
        vin_number: "",
      },
      rules: [(v) => !!v || "Job type is required."],
    };
  },
  computed: {
    // Detect if this is estimate mode
    isEstimateMode() {
      return this.$route.name === 'estimate-create' || this.$route.name === 'estimate-update';
    },
    
    isCreate() {
      return this.$route.name === 'estimate-create' || !this.$route.params.id;
    },
    
    list() {
      return this.$store.getters.technicians;
    },
  },
  methods: {
    async decodeVin() {
      const vm = this;
      let vin = vm.form.vin_number;
      if (!vin) return vm.$alert.show("Please enter VIN Number");
      try {
        vm.$loader.show();
        let url = `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`;
        let data = (await axios.get(url)).data;
        let items = [27, 39, 26, 28, 29, 5, 75, 37];
        let message = "";
        items.forEach((key) => {
          let item = data.Results.find((i) => i.VariableId == key);
          if (item) {
            message += `<b>${item.Variable}:</b> ${item.Value}<br/>`;
          }
        });
        vm.vinDetails = message;
        vm.alert = true;
        vm.$loader.hide();
      } catch (error) {
        vm.$alert.show(error);
        vm.$loader.hide();
      }
    },
    
    onInput() {
      this.$emit("input", this.form);
    },
    
    openAPT() {
      this.form.is_scheduled = true;
      this.$refs.apt.dialog = true;
    },
    
    onTechnicianChange() {
      const vm = this;
      let technician = this.list.find((i) => i.id == this.form.technician);
      if (technician && technician.zones) {
        EventBus.$emit("technician-selected", technician);
        vm.$store.dispatch('reRenderOutsideTechnician', technician);
        let fields = {};
        this.list.forEach((i) => {
          fields[i.id] = `${i.name} ${formatPhone(i.phone)}`;
        });
        let msg = formatMessage({
          message: technician.message || "",
          fields,
          isTech: true,
          options: {},
        });
        let message = `<div style="outline:none" contenteditable="plaintext-only">${msg}</div>
        Following zones are assigned to this technician:
        <br/>
        `;
        message += technician.zones
          .toString()
          .split(",")
          .join("<br/>");
        vm.$refs.zone.init(message);
      } else if (technician) {
        EventBus.$emit("technician-selected", technician);
      }
      vm.onInput();
    },
    
    resetForm() {
      this.form = {
        ...this.form,
        template: "",
        technician: "",
      }
    },
    
    changePaymentType() {
      EventBus.$emit("changePaymentType", this.payment);
    },

    changePaymentToggle(data) {
        if(data.toLowerCase() == 'cash'){
          this.payment = 'cash';
        } else {
          this.payment = 'credit_card';
        }
      }
  },
  mounted() {
    EventBus.$on('emptyJobDetails', this.resetForm);
    EventBus.$on('changePaymentToggle', this.changePaymentToggle);
  },
  beforeMount() {
    const vm = this;
    vm.form = vm.value;
    if (vm.isCreate) {
      vm.form.dispatcher = auth.currentUser.email;
    }
  },
};
</script>

<style lang="scss">
.customer-input {
  max-width: 400px;
}
</style>
